/**
 * Story choreography: temporary, playback-only moves of nodes and links while a story step plays.
 * Nothing here is ever saved into the smap: stored positions, sizes and links stay as they are, and
 * whatever an action changes is undone when the step ends (or when the story stops, see `persist`).
 * A step may store it as `choreography: StoryChoreographyAction[]`; the canvas plays it with
 * `engine.choreograph(actions, clock)`.
 *
 * Times are ms on the caller's clock (the step's narration audio when it has one, so pausing or
 * seeking the audio pauses or seeks the choreography): `at` is when an action starts on that clock.
 *
 * Every action: { kind, at, durationMs?, ease?, persist? }
 *   durationMs  how long its motion takes; each kind has a sensible default.
 *   ease        'smooth' (default) | 'linear' | 'snappy' | 'springy'.
 *   persist     'step' (default): what the action leaves in place goes back when the step ends.
 *               'story': it stays until the story stops.
 *
 * Kinds (ids are node ids, linkIds are link ids):
 *   enter       { ids, style: 'fly-in' | 'pop' | 'grow-from-parent', from?, staggerMs? }
 *               The nodes are hidden until their time, then arrive. `from` (fly-in only):
 *               'left' | 'right' | 'up' | 'down' | 'out' (default: from beyond the group, away from its centre).
 *   emphasize   { ids, style: 'swell' | 'rise' | 'glow', holdMs?, staggerMs? }
 *               Grow, lift (3D: up out of the plane, 2D: lift with a shadow) or glow; held for holdMs
 *               (default 1200), then back.
 *   formation   { ids, shape: 'line' | 'circle' | 'stack' | 'arc', around?, spacing?, holdMs? }
 *               The nodes move into a temporary arrangement around their centre, or around node `around`.
 *               With holdMs they go back after it; without, they hold until the step ends.
 *   orbit       { ids, around, turns?, radius? }
 *               The nodes circle node `around` `turns` times (default 1) and land back where they were.
 *   explode     { ids, center?, distance? }
 *               The nodes fly apart, outward from node `center` (or their centre), then back together.
 *   assemble    { ids, center?, distance? }
 *               The reverse: hidden until their time, they come in from scattered places and gather into place.
 *   swap        { out, in }
 *               The `out` nodes slide aside and fade away; the `in` nodes fade in and take their places, in order.
 *   draw-link   { linkIds, staggerMs? }
 *               Each link is hidden until its time, then draws itself from source to target with a brief flow pulse.
 *   focus-pull  { ids, holdMs? }
 *               These nodes come forward while everything else recedes; without holdMs until the step ends.
 *
 * Example, narration at 0s "Three teams...", 2.5s "...all feeding one pipeline":
 *   [{ kind: 'enter', at: 0, ids: ['a', 'b', 'c'], style: 'pop', staggerMs: 250 },
 *    { kind: 'draw-link', at: 2500, linkIds: ['ab', 'bc'], staggerMs: 300 },
 *    { kind: 'emphasize', at: 4000, ids: ['c'], style: 'glow', holdMs: 1500 }]
 */

export type ChoreographyKind =
  | 'enter'
  | 'emphasize'
  | 'formation'
  | 'orbit'
  | 'explode'
  | 'assemble'
  | 'swap'
  | 'draw-link'
  | 'focus-pull';
export type ChoreographyEase = 'smooth' | 'linear' | 'snappy' | 'springy';
export type ChoreographyPersist = 'step' | 'story';
export type ChoreographyDirection = 'left' | 'right' | 'up' | 'down' | 'out';
export type ChoreographyEnterStyle = 'fly-in' | 'pop' | 'grow-from-parent';
export type ChoreographyEmphasisStyle = 'swell' | 'rise' | 'glow';
export type ChoreographyShape = 'line' | 'circle' | 'stack' | 'arc';

export interface ChoreographyTiming {
  /** When it starts, ms on the caller's clock. */
  at: number;
  durationMs?: number;
  ease?: ChoreographyEase;
  persist?: ChoreographyPersist;
}

export interface ChoreographyEnter extends ChoreographyTiming {
  kind: 'enter';
  ids: string[];
  style: ChoreographyEnterStyle;
  from?: ChoreographyDirection;
  staggerMs?: number;
}

export interface ChoreographyEmphasize extends ChoreographyTiming {
  kind: 'emphasize';
  ids: string[];
  style: ChoreographyEmphasisStyle;
  holdMs?: number;
  staggerMs?: number;
}

export interface ChoreographyFormation extends ChoreographyTiming {
  kind: 'formation';
  ids: string[];
  shape: ChoreographyShape;
  around?: string;
  spacing?: number;
  holdMs?: number;
}

export interface ChoreographyOrbit extends ChoreographyTiming {
  kind: 'orbit';
  ids: string[];
  around: string;
  turns?: number;
  radius?: number;
}

export interface ChoreographyScatter extends ChoreographyTiming {
  kind: 'explode' | 'assemble';
  ids: string[];
  center?: string;
  distance?: number;
}

export interface ChoreographySwap extends ChoreographyTiming {
  kind: 'swap';
  out: string[];
  in: string[];
}

export interface ChoreographyDrawLink extends ChoreographyTiming {
  kind: 'draw-link';
  linkIds: string[];
  staggerMs?: number;
}

export interface ChoreographyFocusPull extends ChoreographyTiming {
  kind: 'focus-pull';
  ids: string[];
  holdMs?: number;
}

export type StoryChoreographyAction =
  | ChoreographyEnter
  | ChoreographyEmphasize
  | ChoreographyFormation
  | ChoreographyOrbit
  | ChoreographyScatter
  | ChoreographySwap
  | ChoreographyDrawLink
  | ChoreographyFocusPull;

export const CHOREOGRAPHY_KINDS: readonly ChoreographyKind[] = [
  'enter',
  'emphasize',
  'formation',
  'orbit',
  'explode',
  'assemble',
  'swap',
  'draw-link',
  'focus-pull',
];

const EASES: readonly ChoreographyEase[] = ['smooth', 'linear', 'snappy', 'springy'];
const DIRECTIONS: readonly ChoreographyDirection[] = ['left', 'right', 'up', 'down', 'out'];
const ENTER_STYLES: readonly ChoreographyEnterStyle[] = ['fly-in', 'pop', 'grow-from-parent'];
const EMPHASIS_STYLES: readonly ChoreographyEmphasisStyle[] = ['swell', 'rise', 'glow'];
const SHAPES: readonly ChoreographyShape[] = ['line', 'circle', 'stack', 'arc'];

const MAX_ACTIONS = 64;
const MAX_IDS = 200;
const MAX_TIME_MS = 3_600_000;
const MAX_DURATION_MS = 60_000;
const MAX_TURNS = 10;
const MAX_DISTANCE = 5000;

function pick<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && allowed.indexOf(value as T) >= 0 ? (value as T) : undefined;
}

function idList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const raw of value) {
    const id = typeof raw === 'string' ? raw.trim() : '';
    if (id && out.indexOf(id) < 0) out.push(id);
    if (out.length >= MAX_IDS) break;
  }
  return out;
}

function idOf(value: unknown): string | undefined {
  const id = typeof value === 'string' ? value.trim() : '';
  return id || undefined;
}

function num(value: unknown, min: number, max: number): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const n = Number(value);
  if (!Number.isFinite(n)) return undefined;
  return Math.min(max, Math.max(min, n));
}

function timing(input: Record<string, unknown>): ChoreographyTiming | null {
  const at = num(input['at'], 0, MAX_TIME_MS);
  if (at === undefined) return null;
  const out: ChoreographyTiming = { at: Math.round(at) };
  const duration = num(input['durationMs'], 0, MAX_DURATION_MS);
  if (duration !== undefined) out.durationMs = Math.round(duration);
  const ease = pick(input['ease'], EASES);
  if (ease) out.ease = ease;
  const persist = pick(input['persist'], ['step', 'story'] as const);
  if (persist) out.persist = persist;
  return out;
}

function withOptional<T extends object>(out: T, key: string, value: unknown): T {
  if (value !== undefined) (out as Record<string, unknown>)[key] = value;
  return out;
}

/** One stored or agent-written action made safe to play; null when it cannot play. */
export function normalizeChoreographyAction(value: unknown): StoryChoreographyAction | null {
  if (!value || typeof value !== 'object') return null;
  const input = value as Record<string, unknown>;
  const kind = pick(input['kind'], CHOREOGRAPHY_KINDS);
  const time = timing(input);
  if (!kind || !time) return null;
  const ids = idList(input['ids']);
  const ms = (key: string) => {
    const n = num(input[key], 0, MAX_DURATION_MS);
    return n === undefined ? undefined : Math.round(n);
  };
  switch (kind) {
    case 'enter': {
      if (ids.length === 0) return null;
      const out: ChoreographyEnter = { kind, ...time, ids, style: pick(input['style'], ENTER_STYLES) ?? 'pop' };
      withOptional(out, 'from', pick(input['from'], DIRECTIONS));
      return withOptional(out, 'staggerMs', ms('staggerMs'));
    }
    case 'emphasize': {
      if (ids.length === 0) return null;
      const out: ChoreographyEmphasize = { kind, ...time, ids, style: pick(input['style'], EMPHASIS_STYLES) ?? 'swell' };
      withOptional(out, 'holdMs', ms('holdMs'));
      return withOptional(out, 'staggerMs', ms('staggerMs'));
    }
    case 'formation': {
      if (ids.length === 0) return null;
      const out: ChoreographyFormation = { kind, ...time, ids, shape: pick(input['shape'], SHAPES) ?? 'circle' };
      withOptional(out, 'around', idOf(input['around']));
      withOptional(out, 'spacing', num(input['spacing'], 10, MAX_DISTANCE));
      return withOptional(out, 'holdMs', ms('holdMs'));
    }
    case 'orbit': {
      const around = idOf(input['around']);
      if (ids.length === 0 || !around) return null;
      const out: ChoreographyOrbit = { kind, ...time, ids, around };
      withOptional(out, 'turns', num(input['turns'], 0.25, MAX_TURNS));
      return withOptional(out, 'radius', num(input['radius'], 10, MAX_DISTANCE));
    }
    case 'explode':
    case 'assemble': {
      if (ids.length === 0) return null;
      const out: ChoreographyScatter = { kind, ...time, ids };
      withOptional(out, 'center', idOf(input['center']));
      return withOptional(out, 'distance', num(input['distance'], 10, MAX_DISTANCE));
    }
    case 'swap': {
      const outIds = idList(input['out']);
      const inIds = idList(input['in']);
      if (outIds.length === 0 && inIds.length === 0) return null;
      return { kind, ...time, out: outIds, in: inIds };
    }
    case 'draw-link': {
      const linkIds = idList(input['linkIds']);
      if (linkIds.length === 0) return null;
      const out: ChoreographyDrawLink = { kind, ...time, linkIds };
      return withOptional(out, 'staggerMs', ms('staggerMs'));
    }
    case 'focus-pull': {
      if (ids.length === 0) return null;
      const out: ChoreographyFocusPull = { kind, ...time, ids };
      return withOptional(out, 'holdMs', ms('holdMs'));
    }
  }
  return null;
}

/**
 * A stored or agent-written choreography (an array of actions, or `{ actions }`) made safe to play:
 * unknown kinds and actions without ids or a start time are dropped, numbers are clamped, and the
 * actions come back in start order.
 */
export function normalizeStoryChoreography(value: unknown): StoryChoreographyAction[] {
  const list =
    Array.isArray(value)
      ? value
      : value && typeof value === 'object' && Array.isArray((value as Record<string, unknown>)['actions'])
        ? ((value as Record<string, unknown>)['actions'] as unknown[])
        : [];
  const out: { action: StoryChoreographyAction; index: number }[] = [];
  for (let i = 0; i < list.length && out.length < MAX_ACTIONS; i++) {
    const action = normalizeChoreographyAction(list[i]);
    if (action) out.push({ action, index: i });
  }
  out.sort((a, b) => a.action.at - b.action.at || a.index - b.index);
  return out.map((entry) => entry.action);
}

/**
 * A step's choreography with every node and link id translated by `remap`, e.g. for a copied or
 * imported smap: ids with no counterpart are dropped, and so is an action left with nothing to move.
 */
export function remapStoryChoreographyIds(
  value: unknown,
  remap: (id: string) => string | undefined
): StoryChoreographyAction[] {
  const list = (ids: readonly string[]) => ids.map((id) => remap(id)).filter((id): id is string => !!id);
  const one = (id: string | undefined) => (id ? remap(id) : undefined);
  const mapped = normalizeStoryChoreography(value).map((action): Record<string, unknown> => {
    switch (action.kind) {
      case 'swap':
        return { ...action, out: list(action.out), in: list(action.in) };
      case 'draw-link':
        return { ...action, linkIds: list(action.linkIds) };
      case 'orbit':
      case 'formation':
        return { ...action, ids: list(action.ids), around: one(action.around) };
      case 'explode':
      case 'assemble':
        return { ...action, ids: list(action.ids), center: one(action.center) };
      default:
        return { ...action, ids: list(action.ids) };
    }
  });
  return normalizeStoryChoreography(mapped);
}
